import { DistributorOnly } from '@/components/Helpers/DistributorOnly'
import { HideForCustomer } from '@/components/Helpers/HideForCustomer'
import T, { useTranslate } from '@/components/Helpers/Translate'
import { Button } from '@/components/_ui/Button'
import { Popover } from '@/components/_ui/Popover'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { usePriceHandler } from '@/lib/hooks/helpers/usePriceHandler'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import { getTerms } from '@/lib/hooks/services/useGetTerms'
import {
  generateAddToCartEventData,
  generateCartEventData,
  generateCartItemEventData,
  getCartItemsCount,
} from '@/lib/services/mixpanel/helpers/generateCartEventData'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { isProductOptionSubscription } from '@/lib/utils/shared/IsProductOptionSubscription'
import { customerTypeHasSubscriptionDiscount } from '@/lib/utils/shared/customerTypeHasSubscriptionDiscount'
import type { CartItemType } from '@/store/types/cart'
import useGlobalStore from '@/store/useGlobalStore'
import { PopoverContent, PopoverTrigger } from '@radix-ui/react-popover'
import Image from 'next/image'
import { useState } from 'react'
import { useFocusMode } from '@/lib/hooks/helpers/useFocusMode'
import { cn } from '@/lib/utils'
import { useOrderLimitCheck } from '@/lib/hooks/services/useOrderLimitCheck'
import { useNfrCheck } from '@/lib/hooks/services/useNfrCheck'
import { CartSheetSubscribeAndSaveCheckbox } from './CartSheetSubscribeAndSaveCheckbox'

const CartSheetProductItem = ({ cartItem }: { cartItem: CartItemType }) => {
  const { removeFromCart, updateCartItemQty } = useGlobalStore()
  const {
    user,
    cart,
    app: { marketExceptions },
  } = useGlobalStore()
  const { priceHandler } = usePriceHandler()
  const { gtm } = useGTM()
  const { clientRedirect } = useClientRedirect()
  const { isFocusMode, isLoadingIsFocusMode } = useFocusMode()
  const { isNfrUser, orderLimit } = useNfrCheck()
  const { isLimitAlreadyInCart } = useOrderLimitCheck({
    limit: orderLimit,
    currentCount: cartItem.qty,
    sku: cartItem.product.sku,
  })
  const { translate } = useTranslate()

  const variationImage = cartItem.image
  const flavorString = cartItem.product.combinationName
  const [isConfirmRemove, setIsConfirmRemove] = useState(false)

  const handleIncrementQuantity = () => {
    gtm.addToCart({ cartItem, event_location: 'cart_increase_item_quantity' })
    updateCartItemQty({
      ...cartItem,
      qty: cartItem.qty + 1,
      addToCartLocation: [
        ...(cartItem.addToCartLocation || []),
        'cart_increase_item_quantity',
      ],
    })

    mixpanelService
      .trackEvent(MixpanelEvent.ADD_TO_CART, {
        event_location: 'cart_increase_item_quantity',
        cart: generateAddToCartEventData({
          cartItem,
          currency: marketExceptions?.currencyCode || 'USD',
          price:
            getTerms({
              forCustomerType: user.customerType,
              pricingData: cartItem.product.pricing,
            })?.priceEach || 0,
          quantity: 1,
          addToCartLocation: 'cart_increase_item_quantity',
          focus: false,
        }),
        item_count: 1,
      })
      .catch(() => {})

    setTimeout(() => {
      // Run with delay so it runs after everything else
      gtm.viewCart([...cart.otp, ...cart.subscribe])
      mixpanelService
        .trackEvent(MixpanelEvent.VIEW_CART, {
          cart: generateCartEventData(
            cart,
            marketExceptions?.currencyCode || 'USD',
            user.customerType,
          ),
          item_count: getCartItemsCount(cart),
        })
        .catch(() => {})
    }, 200)
  }
  const handleDecrementQuantity = () => {
    if (cartItem.qty === 1) {
      setIsConfirmRemove(true)

      return
    }
    gtm.removeFromCart({
      cartItem,
      qty: 1,
      event_location: 'cart_decrease_item_quantity',
    })

    updateCartItemQty({
      ...cartItem,
      qty: cartItem.qty - 1,
      addToCartLocation: [...(cartItem.addToCartLocation || [])].slice(0, -1),
    })

    const removedItem = {
      ...cartItem,
      addToCartLocation: [...(cartItem.addToCartLocation || [])].slice(
        cartItem.qty - 1,
      ),
      qty: 1,
    }

    mixpanelService
      .trackEvent(MixpanelEvent.REMOVE_FROM_CART, {
        cart: generateCartItemEventData(
          removedItem,
          marketExceptions?.currencyCode || 'USD',
          getTerms({
            forCustomerType: user.customerType,
            pricingData: cartItem.product.pricing,
          })?.priceEach || 0,
        ),
      })
      .catch(() => {})

    setTimeout(() => {
      // Run with delay so it runs after everything else
      gtm.viewCart([...cart.otp, ...cart.subscribe])

      mixpanelService
        .trackEvent(MixpanelEvent.VIEW_CART, {
          cart: generateCartEventData(
            cart,
            marketExceptions?.currencyCode || 'USD',
            user.customerType,
          ),
          item_count: getCartItemsCount(cart),
        })
        .catch(() => {})
    }, 200)
  }

  const removeFromCartPopoverContent = () => (
    <div className="mr-3 flex flex-col items-center space-y-3 rounded-lg border bg-white p-5 shadow-md">
      <span>
        <T>are_you_sure</T>
      </span>
      <Button
        onClick={() => {
          gtm.removeFromCart({
            cartItem,
            qty: cartItem.qty,
            event_location: 'cart_decrease_item_quantity',
          })

          mixpanelService
            .trackEvent(MixpanelEvent.REMOVE_FROM_CART, {
              cart: generateCartItemEventData(
                cartItem,
                marketExceptions?.currencyCode || 'USD',
                getTerms({
                  forCustomerType: user.customerType,
                  pricingData: cartItem.product.pricing,
                })?.priceEach || 0,
              ),
            })
            .catch(() => {})

          setTimeout(() => {
            mixpanelService
              .trackEvent(MixpanelEvent.VIEW_CART, {
                cart: generateCartEventData(
                  cart,
                  marketExceptions?.currencyCode || 'USD',
                  user.customerType,
                ),
                item_count: getCartItemsCount(cart),
              })
              .catch(() => {})
          })
          removeFromCart(cartItem)
        }}
        variant="destructive"
      >
        <T>yes_remove_from_cart</T>
      </Button>
    </div>
  )

  const getProductPricing = () => {
    const isSubscription = isProductOptionSubscription({ po: cartItem.po })

    if (
      isSubscription &&
      customerTypeHasSubscriptionDiscount(user.customerType)
    ) {
      return priceHandler({
        pricingData: cartItem.product.pricing,
        forCustomerType: user.customerType,
        subscriptionDiscount: cartItem.subscriptionDiscount,
        quantity: cartItem.qty,
      })
    }

    return priceHandler({
      pricingData: cartItem.product.pricing,
      quantity: cartItem.qty,
    })
  }

  const navigateToProductPage = async () => {
    await clientRedirect.to({
      path: `/product/${cartItem.slug}?sku=${cartItem.product.sku}`,
    })
  }

  return (
    <div
      className="flex justify-between border-b-2 border-slate-200 p-4"
      data-test={`cart-item-${cartItem.slug}`}
    >
      <div className="flex w-full">
        {/* Don't allow product image to be clickable in foucs mode */}
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'flex w-[25%] items-center',
            !isFocusMode && !isLoadingIsFocusMode && 'cursor-pointer',
          )}
          onClick={
            !isFocusMode && !isLoadingIsFocusMode
              ? navigateToProductPage
              : undefined
          }
          onKeyDown={async (event) => {
            if (
              !isFocusMode &&
              !isLoadingIsFocusMode &&
              (event.key === 'Enter' || event.key === ' ')
            ) {
              try {
                await navigateToProductPage()
              } catch (error) {
                console.error('Error navigating to product page:', error)
              }
            }
          }}
          aria-label="View Product Details"
        >
          <Image
            height={100}
            width={100}
            src={variationImage || cartItem.image}
            alt="Product"
          />
        </div>

        <div className="m-auto ml-2 flex-1 flex-col items-center justify-center">
          <div
            role="button"
            tabIndex={0}
            className={cn(
              !isFocusMode && !isLoadingIsFocusMode && 'cursor-pointer',
            )}
            onClick={
              !isFocusMode && !isLoadingIsFocusMode
                ? navigateToProductPage
                : undefined
            }
            onKeyDown={async (event) => {
              if (
                !isFocusMode &&
                !isLoadingIsFocusMode &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                try {
                  await navigateToProductPage()
                } catch (error) {
                  console.error('Error navigating to product page:', error)
                }
              }
            }}
            aria-label={`View details for ${cartItem.title}`}
          >
            <h2 className="text-lg text-primary">{cartItem.title}</h2>
            <div className="pb-2 text-sm text-[#0B3B6199]">{flavorString}</div>
            <DistributorOnly className="flex space-x-1">
              <span>
                <T>pv</T>
              </span>
              <span>
                {
                  getTerms({
                    pricingData: cartItem.product.pricing,
                    forCustomerType: user.customerType,
                  })?.pvEach
                }
              </span>
            </DistributorOnly>
          </div>

          {!isNfrUser && (
            <div className="flex items-center space-x-2">
              <CartSheetSubscribeAndSaveCheckbox cartItem={cartItem} />
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end justify-between">
        <h2 className="text-lg text-primary">{getProductPricing()}</h2>
        <HideForCustomer>
          <h2 className="text-sm text-secondary line-through">
            {priceHandler({
              pricingData: cartItem.product.pricing,
              forCustomerType: 'Customer',
              quantity: cartItem.qty,
            })}
          </h2>
        </HideForCustomer>

        <div className="mt-2 flex h-[30px] items-center justify-center rounded-lg border border-secondary">
          <Popover
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                setIsConfirmRemove(isOpen)
              }
            }}
            open={isConfirmRemove}
          >
            <PopoverTrigger>
              <Button
                onClick={() => handleDecrementQuantity()}
                variant="icon"
                className="h-full p-2"
                data-test={`cart-decrement-button-${cartItem.index}`}
              >
                -
              </Button>
            </PopoverTrigger>
            <PopoverContent style={{ zIndex: 1002 }}>
              {removeFromCartPopoverContent()}
            </PopoverContent>
          </Popover>

          <span>{cartItem.qty}</span>
          <Button
            onClick={() => handleIncrementQuantity()}
            variant="icon"
            className="h-full p-2"
            data-test={`cart-increment-button-${cartItem.index}`}
            disabled={Boolean(orderLimit) && isLimitAlreadyInCart}
          >
            +
          </Button>
        </div>
        {Boolean(orderLimit) && isLimitAlreadyInCart && (
          <span
            className="mt-2 flex items-center text-sm whitespace-nowrap text-red-500"
            data-test="cart-limit-reached-message"
          >
            {translate(
              'order_limit_max_cart',
              {
                orderLimit: orderLimit?.toString() || '',
              },
              true,
            )}
          </span>
        )}
        <Popover>
          <PopoverTrigger>
            <Button
              className="mt-1"
              variant="icon"
              data-test={`cart-remove-button-${cartItem.index}`}
            >
              <Image
                height={14}
                width={14}
                src="/icons/trash.svg"
                alt="Remove"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent style={{ zIndex: 1002 }}>
            {removeFromCartPopoverContent()}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export default CartSheetProductItem
